import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Bill {
    id: string;
    tenantId: string;
    billNumber: string;
    vendorId?: string;
    vendorName?: string;
    vendorEmail?: string;
    amount: number;
    taxAmount: number;
    totalAmount: number;
    status: string;
    dueDate: string;
    receivedDate: string;
    createdAt: string;
    updatedAt: string;
}

interface BillState {
    bills: Bill[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchBills: (filters?: any) => Promise<void>;
    createBill: (data: Partial<Bill>) => Promise<void>;
    updateBill: (id: string, data: Partial<Bill>) => Promise<void>;
}

export const useBillStore = create<BillState>((set, get) => ({
    bills: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchBills: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/money/bills', { params: filters });
            const { data, pagination } = response.data;
            set({ bills: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch bills', isLoading: false });
        }
    },
    createBill: async (data: Partial<Bill>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/money/bills', data);
            const newBill = response.data;
            set((state) => ({
                bills: [newBill, ...state.bills],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create bill', isLoading: false });
            throw error;
        }
    },
    updateBill: async (id: string, data: Partial<Bill>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/money/bills/${id}`, data);
            const updatedBill = response.data;
            set((state) => ({
                bills: state.bills.map((o) => (o.id === id ? updatedBill : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update bill', isLoading: false });
            throw error;
        }
    },
}));
